import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, Textarea } from "@/ui";
import { useState } from "react";
import { Mail, MapPin, Phone } from "lucide-react";
const contactImage = "/assets/website/images/Healthcare_professional_communication_contact_0e84d65b.png";
import { apiUrl } from "@/constants";

interface ContactFormData {
  name: string;
  email: string;
  organization: string;
  role: string;
  inquiryType: string;
  message: string;
}


export default function ContactPartnership() {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    organization: '',
    role: '',
    inquiryType: '',
    message: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch(apiUrl('/api/contact/submit'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit contact request');
      }

      const result = await response.json();
      
      // Redirect to thank you page with user data
      const params = new URLSearchParams({
        name: encodeURIComponent(formData.name),
        email: encodeURIComponent(formData.email),
        organization: encodeURIComponent(formData.organization || ''),
        inquiryType: encodeURIComponent(formData.inquiryType || ''),
        submittedAt: result.submittedAt || new Date().toISOString()
      });
      
      window.location.href = `/contact/thank-you?${params.toString()}`;

    } catch (error) {
      console.error('Error submitting contact request:', error);
      alert(`There was an error submitting your request: ${error instanceof Error ? error.message : 'Please try again.'}`);
    }
  };

  const handleInputChange = (field: keyof ContactFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <section id="contact" className="py-20">
      {/* Hero Image Section */}
      <div className="relative h-80 mb-20 overflow-hidden">
        <img 
          src={contactImage} 
          alt="Healthcare Professional Communication Contact"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/60" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-normal text-primary mb-4" data-testid="text-contact-title" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
              Get Started with AIscendus
            </h2>
            <p className="text-xl text-foreground max-w-3xl mx-auto" data-testid="text-contact-description" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
              Ready to transform your healthcare workflows? Contact us for a personalized demo or explore 
              partnership opportunities to bring AI-powered healthcare solutions to your organization.
            </p>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto max-w-7xl">

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto mb-16">
          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-normal text-foreground mb-6" data-testid="text-contact-info-title" style={{ fontFamily: "Montserrat, system-ui, sans-serif" }}>
                Contact Information
              </h3>
              <div className="space-y-6">
                <div className="flex items-start gap-4" data-testid="contact-email">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Email</h4>
                    <p className="text-muted-foreground">contact@aiscendus.com</p>
                    <p className="text-sm text-muted-foreground">Response within 24 hours</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4" data-testid="contact-phone">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                    <Phone className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Phone</h4>
                    <p className="text-muted-foreground">508.718.5990</p>
                    <p className="text-sm text-muted-foreground">EST 9AM–5PM</p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-address">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg mt-1">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-foreground mb-1">US Headquarters</h4>
                      <p className="text-muted-foreground">AIscendus Inc.</p>
                      <p className="text-muted-foreground">3 Allied Dr, Ste 303</p>
                      <p className="text-muted-foreground">Dedham, Massachusetts, 02026</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground mb-1">Offshore Office <span className="text-xs font-normal text-primary">(Coming Up)</span></h4>
                      <p className="text-muted-foreground">AIscendus Limited</p>
                      <p className="text-muted-foreground">Chennai, Tamil Nadu</p>
                      <p className="text-muted-foreground">India</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <Card data-testid="contact-form-card">
            <CardHeader>
              <CardTitle data-testid="text-form-title">Request for Information</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      data-testid="input-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="organization">Organization</Label>
                  <Input
                    id="organization"
                    value={formData.organization}
                    onChange={(e) => handleInputChange('organization', e.target.value)}
                    placeholder="Hospital, Clinic, Health System..."
                    data-testid="input-organization"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="role">Your Role</Label>
                    <select
                      id="role"
                      value={formData.role}
                      onChange={(e) => handleInputChange("role", e.target.value)}
                      className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      data-testid="select-role"
                    >
                      <option value="" disabled>
                        Select your role
                      </option>
                      <option value="physician">Physician</option>
                      <option value="nurse">Nurse</option>
                      <option value="administrator">Healthcare Administrator</option>
                      <option value="it">IT Professional</option>
                      <option value="executive">C-Level Executive</option>
                      <option value="consultant">Healthcare Consultant</option>
                      <option value="vendor">Technology Vendor</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="inquiryType">Inquiry Type</Label>
                    <select
                      id="inquiryType"
                      value={formData.inquiryType}
                      onChange={(e) => handleInputChange("inquiryType", e.target.value)}
                      className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      data-testid="select-inquiry"
                    >
                      <option value="" disabled>
                        Select inquiry type
                      </option>
                      <option value="demo">Product Demo</option>
                      <option value="partnership">Partnership Inquiry</option>
                      <option value="integration">Integration Support</option>
                      <option value="pricing">Pricing Information</option>
                      <option value="general">General Information</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    placeholder="Tell us about your organization's needs, current challenges, or partnership interests..."
                    className="min-h-[120px]"
                    data-testid="textarea-message"
                  />
                </div>

                <Button type="submit" className="w-full" data-testid="button-submit-form">
                  Send Request
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  By submitting this form, you agree to our privacy policy and terms of service.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}